import {useCallback, useMemo} from 'react';
import {createSelector, type Selector} from 'reselect';
import {type PanelType} from 'src/util/panelTypes';

import {useMemoizedSelector} from '../../../../state/hooks';
import * as ViewHooks from '../../../../state/views/hooks';
import * as PanelActions from '../../../../state/views/panel/actions';
import type * as PanelTypes from '../../../../state/views/panel/types';
import {type Ref as SectionRef} from '../../../../state/views/panelBankSectionConfig/types';
import {type RootState} from '../../../../types/redux';
import {usePanelsSharedRefsContext} from '../../../Views/contexts/PanelsSharedRefsContext';

const selectPanel = (panelRef: PanelTypes.Ref) => (state: RootState) =>
  state.views.parts[panelRef.type][panelRef.id];

function makePanelConfigSelector<TConfig, TViewType extends PanelType>(
  panelRef: PanelTypes.Ref
): Selector<
  RootState,
  {viewType: TViewType; config: TConfig; panelId: string}
> {
  return createSelector(selectPanel(panelRef), panel => ({
    viewType: panel.viewType as TViewType,
    config: panel.config as TConfig,
    panelId: panel.__id__,
  }));
}

type Refs = {
  panelRef: PanelTypes.Ref;
  panelBankSectionConfigRef: SectionRef;
};

type PanelSettingsConfigUpdates<TConfig, TViewType> = {
  config: TConfig;
  viewType: TViewType;
  updateConfig: (configUpdate: Partial<TConfig>) => void;
  unsetConfigField: (field: keyof TConfig) => void;
  /** Unsets several fields in one dispatch (one undo entry), for grouped settings. */
  unsetConfigFields: (fields: Array<keyof TConfig>) => void;
  panelId: string;
};
export function usePanelSettingsConfigUpdates<
  TConfig extends PanelTypes.Panel['config'],
  TViewType extends PanelType,
>({
  panelRef,
  panelBankSectionConfigRef,
}: Refs): PanelSettingsConfigUpdates<TConfig, TViewType> {
  const {config, viewType, panelId} = useMemoizedSelector(
    makePanelConfigSelector<TConfig, TViewType>,
    panelRef
  );
  const {panelBankConfigRef} = usePanelsSharedRefsContext();

  const updatePanelConfig = useCallback(
    (ref: PanelTypes.Ref, configUpdate: Partial<PanelTypes.Panel['config']>) =>
      PanelActions.updateConfig(
        ref,
        configUpdate,
        panelBankSectionConfigRef,
        panelBankConfigRef
      ),
    [panelBankSectionConfigRef, panelBankConfigRef]
  );
  const updateConfig = ViewHooks.useViewAction(panelRef, updatePanelConfig);
  // This is needed because the `updateConfig` action doesn't support unsetting a field
  // since it just merges the update over the old one, so setting a field to undefined
  // doesn't work.
  const unsetPanelConfigField = useCallback(
    (ref: PanelTypes.Ref, field: keyof TConfig) => {
      const {[field]: _, ...newConfig} = config as TConfig; // casting here because we already checked the panel config type
      return PanelActions.setConfig(
        ref,
        newConfig,
        undefined,
        undefined,
        panelBankSectionConfigRef,
        panelBankConfigRef
      );
    },
    [config, panelBankSectionConfigRef, panelBankConfigRef]
  );
  const unsetConfigField = ViewHooks.useViewAction(
    panelRef,
    unsetPanelConfigField
  );
  const unsetPanelConfigFields = useCallback(
    (ref: PanelTypes.Ref, fields: Array<keyof TConfig>) => {
      const newConfig = {...(config as TConfig)};
      for (const field of fields) {
        delete newConfig[field];
      }
      return PanelActions.setConfig(
        ref,
        newConfig,
        undefined,
        undefined,
        panelBankSectionConfigRef,
        panelBankConfigRef
      );
    },
    [config, panelBankSectionConfigRef, panelBankConfigRef]
  );
  const unsetConfigFields = ViewHooks.useViewAction(
    panelRef,
    unsetPanelConfigFields
  );
  return useMemo(
    () => ({
      config,
      viewType,
      updateConfig,
      unsetConfigField,
      unsetConfigFields,
      panelId,
    }),
    [
      config,
      viewType,
      updateConfig,
      unsetConfigField,
      unsetConfigFields,
      panelId,
    ]
  );
}
